/**
 * LinhaSelecao.ts — Preview tracejado das rotas: de CADA origem selecionada ate
 * o cursor (Fase 6: varias origens). Ponta de seta se o cursor esta num planeta.
 * Tambem desenha o anel girante dos planetas selecionados.
 */

import { Graphics } from 'pixi.js';
import gsap from 'gsap';
import { corPlaneta } from '../config';
import type { Planeta, EstadoJogo } from '../domain/tipos';
import type { Camera } from './Camera';
import type { PixiApp } from './PixiApp';

export class LinhaSelecao {
  private sprite: Graphics | null = null;
  private aneis: Graphics | null = null;
  private tempo = 0;

  constructor(
    private readonly pixi: PixiApp,
    private readonly estado: EstadoJogo,
    private readonly camera: Camera,
  ) {}

  /** Liga o desenho dos aneis de selecao (a cada frame). */
  iniciar(getSelecionados: () => number[]): void {
    this.pixi.addTicker((t) => {
      this.tempo += t.deltaMS / 1000;
      this.desenharAneis(getSelecionados());
    });
  }

  /** Desenha as linhas das origens ate as coordenadas de tela do cursor. */
  desenhar(origens: number[], sx: number, sy: number): void {
    if (!this.sprite) {
      this.sprite = new Graphics();
      this.pixi.uiLayer.addChild(this.sprite);
    }
    const g = this.sprite;
    g.clear();
    if (origens.length === 0) return;

    const mundo = this.camera.telaParaMundo(sx, sy);
    const alvo = planetaEm(this.estado, mundo.x, mundo.y);
    const fimX = alvo ? alvo.x : mundo.x;
    const fimY = alvo ? alvo.y : mundo.y;
    const corLinha = corPlaneta(this.estado.suaCor);

    for (const origemId of origens) {
      const origem = this.estado.planetas.get(origemId);
      if (!origem || (alvo && alvo.id === origem.id)) continue;
      const dx = fimX - origem.x;
      const dy = fimY - origem.y;
      const dist = Math.hypot(dx, dy);
      if (dist < 1) continue;
      const nx = dx / dist;
      const ny = dy / dist;

      const startX = origem.x + nx * (origem.raio + 8);
      const startY = origem.y + ny * (origem.raio + 8);
      const endX = alvo ? fimX - nx * (alvo.raio + 8) : fimX;
      const endY = alvo ? fimY - ny * (alvo.raio + 8) : fimY;
      const distRota = Math.hypot(endX - startX, endY - startY);

      const seg = 14;
      const gap = 8;
      const n = Math.floor(distRota / (seg + gap));
      for (let i = 0; i < n; i++) {
        const t1 = i * (seg + gap);
        const t2 = t1 + seg;
        g.moveTo(startX + nx * t1, startY + ny * t1).lineTo(startX + nx * t2, startY + ny * t2)
          .stroke({ color: corLinha, width: 2, alpha: 0.7 });
      }

      if (alvo) {
        const ang = Math.atan2(endY - startY, endX - startX);
        g.moveTo(endX, endY).lineTo(endX - 12 * Math.cos(ang - 0.4), endY - 12 * Math.sin(ang - 0.4))
          .stroke({ color: corLinha, width: 2, alpha: 0.9 });
        g.moveTo(endX, endY).lineTo(endX - 12 * Math.cos(ang + 0.4), endY - 12 * Math.sin(ang + 0.4))
          .stroke({ color: corLinha, width: 2, alpha: 0.9 });
      }
    }
  }

  /** Apaga as linhas (selecao cancelada). */
  limpar(): void {
    this.sprite?.clear();
  }

  private desenharAneis(ids: number[]): void {
    if (!this.aneis) {
      this.aneis = new Graphics();
      this.aneis.blendMode = 'add';
      this.pixi.uiLayer.addChild(this.aneis);
    }
    const g = this.aneis;
    g.clear();
    if (ids.length === 0) return;
    const cor = corPlaneta(this.estado.suaCor);
    const giro = this.tempo * 0.9;
    for (const id of ids) {
      const p = this.estado.planetas.get(id);
      if (!p) continue;
      const r = p.raio + 26;
      const n = 12;
      for (let i = 0; i < n; i++) {
        const a0 = giro + (i / n) * Math.PI * 2;
        const a1 = a0 + (Math.PI * 2 / n) * 0.55;
        g.arc(p.x, p.y, r, a0, a1).stroke({ color: cor, width: 2.5, alpha: 0.85 });
        g.closePath();
      }
      g.circle(p.x, p.y, r - 5).stroke({ color: cor, width: 1, alpha: 0.25 });
    }
  }

  remover(): void {
    if (this.sprite) {
      if (this.sprite.parent) this.sprite.parent.removeChild(this.sprite);
      this.sprite.destroy();
      this.sprite = null;
    }
  }
}

// ---------------------------------------------------------------------------
// Helpers compartilhados com Selecao
// ---------------------------------------------------------------------------

export function planetaEm(estado: EstadoJogo, mx: number, my: number): Planeta | null {
  for (const p of estado.planetas.values()) {
    const dx = p.x - mx;
    const dy = p.y - my;
    if (dx * dx + dy * dy <= (p.raio + 12) * (p.raio + 12)) return p;
  }
  return null;
}

/** Pulsa o planeta — usado no feedback de "nao pode selecionar". */
export function pulsarPlaneta(p: Planeta): void {
  if (!p.spriteContainer) return;
  gsap.fromTo(
    p.spriteContainer.scale,
    { x: 1, y: 1 },
    { x: 1.15, y: 1.15, duration: 0.15, yoyo: true, repeat: 1 },
  );
}
